import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useLanguage } from '@/lib/i18n';
import { FileText, Search, CheckCircle, Award, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';

interface RecognitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const RecognitionDialog = ({ open, onOpenChange }: RecognitionDialogProps) => {
  const { tr } = useLanguage();
  const steps = [
    { icon: FileText, label: tr('recognition.step1') },
    { icon: Search, label: tr('recognition.step2') },
    { icon: CheckCircle, label: tr('recognition.step3'), sub: tr('recognition.step3.sub') },
    { icon: Award, label: tr('recognition.step4'), sub: tr('recognition.step4.sub') },
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-2 text-center">
          {tr('recognition.title')}
        </h2>
        <p className="mb-4 text-muted-foreground text-center">{tr('recognition.subtitle')}</p>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {[1, 2].map((num) => (
            <div key={num} className="bg-card rounded-xl border border-border p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  {num === 1 ? (
                    <Search className="w-5 h-5 text-primary" />
                  ) : (
                    <FileText className="w-5 h-5 text-primary" />
                  )}
                </div>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground">
                    {tr(`recognition.box${num}.title`)}
                  </h3>
                  <span className="text-xs text-muted-foreground italic">
                    ({tr(`recognition.box${num}.titleLocal`)})
                  </span>
                </div>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {tr(`recognition.box${num}.desc`)}
              </p>
            </div>
          ))}
        </div>
        <div className="mb-6">
          <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-4">
            {steps.map((step, i) => (
              <div key={i} className="relative flex flex-col items-center text-center">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-base font-bold mb-2 relative z-10">
                  {i + 1}
                </div>
                <step.icon className="w-5 h-5 text-primary mb-1" />
                <p className="font-heading text-xs font-semibold text-foreground">{step.label}</p>
                {step.sub && (
                  <span className="text-xs text-muted-foreground mt-1">({step.sub})</span>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex items-start gap-3 mb-4">
          <ShieldCheck className="w-6 h-6 text-primary shrink-0 mt-0.5" />
          <p className="text-foreground text-xs md:text-sm leading-relaxed font-medium">
            {tr('recognition.info')}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RecognitionDialog;
